// app/javascript/components/student/ResumeAttemptModal.tsx
import React, { useState, useEffect } from 'react'
import { router } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Clock, AlertTriangle, ArrowRight, X, Loader2 } from 'lucide-react'
import type { OngoingAttempt } from '@/types'

interface ResumeAttemptModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ongoingAttempt: OngoingAttempt | null
  onAbandonSuccess?: () => void
  title?: string
  description?: string
}

function formatTimeLeft(seconds: number): string {
  if (seconds <= 0) return 'Time up'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) {
    return `${hours}h ${minutes}m left`
  }
  return `${minutes}m left`
}

export function ResumeAttemptModal({
  open,
  onOpenChange,
  ongoingAttempt,
  onAbandonSuccess,
  title = 'Resume your exam?',
  description = 'You already have an exam in progress. Resume where you left off, or abandon it to start the new one.',
}: ResumeAttemptModalProps) {
  const [secondsRemaining, setSecondsRemaining] = useState<number>(() => ongoingAttempt?.remaining_seconds || 0)
  const [sectionSeconds, setSectionSeconds] = useState<number | null>(() => ongoingAttempt?.active_section_remaining_seconds ?? null)
  const [isAbandoning, setIsAbandoning] = useState(false)

  useEffect(() => {
    if (ongoingAttempt?.remaining_seconds != null) {
      setSecondsRemaining(ongoingAttempt.remaining_seconds)
    }
    if (ongoingAttempt?.active_section_remaining_seconds != null) {
      setSectionSeconds(ongoingAttempt.active_section_remaining_seconds)
    } else {
      setSectionSeconds(null)
    }
  }, [ongoingAttempt])

  useEffect(() => {
    if (!open || secondsRemaining <= 0) return

    const timer = setInterval(() => {
      setSecondsRemaining((prev) => Math.max(0, prev - 1))
      setSectionSeconds((prev) => (prev != null ? Math.max(0, prev - 1) : null))
    }, 1000)

    return () => clearInterval(timer)
  }, [open, secondsRemaining])

  if (!ongoingAttempt) return null

  const handleResume = () => {
    onOpenChange(false)
    router.visit(ongoingAttempt.resume_url)
  }

  const handleAbandon = async () => {
    if (isAbandoning) return
    setIsAbandoning(true)

    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    try {
      const response = await fetch(`/student/attempts/${ongoingAttempt.id}/abandon`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': token,
        },
      })

      if (response.ok) {
        setIsAbandoning(false)
        onOpenChange(false)
        onAbandonSuccess?.()
      } else {
        setIsAbandoning(false)
      }
    } catch (e) {
      console.error('Failed to abandon attempt', e)
      setIsAbandoning(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card/95 backdrop-blur-xl border-border shadow-glass-lg p-6">
        <DialogHeader className="space-y-2">
          <DialogTitle className="text-xl font-bold text-foreground">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Ongoing Attempt Highlight Card */}
          <div className="p-4 rounded-xl bg-tertiary/10 border border-tertiary/30 shadow-xs space-y-2">
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-[10px] font-extrabold uppercase tracking-wider text-tertiary border-tertiary/40 bg-tertiary/10">
                In Progress
              </Badge>
              {ongoingAttempt.exam_name && (
                <span className="text-[11px] font-medium text-muted-foreground truncate max-w-[180px]">
                  {ongoingAttempt.exam_name}
                </span>
              )}
            </div>

            <h3 className="text-lg font-bold text-foreground tracking-tight">
              {ongoingAttempt.test_title}
            </h3>

            <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-tertiary">
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 shrink-0" />
                <span>
                  {ongoingAttempt.is_sectional && sectionSeconds != null
                    ? `${formatTimeLeft(sectionSeconds)}${ongoingAttempt.active_section_name ? ` (${ongoingAttempt.active_section_name})` : ''}`
                    : formatTimeLeft(secondsRemaining)}
                </span>
              </div>
              {ongoingAttempt.is_sectional && sectionSeconds != null && secondsRemaining > 0 && (
                <span className="text-[11px] font-normal text-muted-foreground">
                  • {formatTimeLeft(secondsRemaining)} total test time
                </span>
              )}
            </div>
          </div>

          {/* Warning Callout Box */}
          <div className="p-3.5 rounded-xl border border-destructive/30 bg-destructive/10 flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-xs text-destructive font-medium leading-relaxed">
              Abandoning ends the current attempt permanently. Your answers won't be saved.
            </p>
          </div>
        </div>

        {/* Modal Actions */}
        <div className="flex flex-col sm:flex-row items-center gap-2.5 pt-2">
          <Button
            onClick={handleResume}
            className="w-full sm:flex-1 h-10 font-bold bg-primary text-primary-fg hover:bg-primary/90 rounded-xl gap-1.5 shadow-xs"
          >
            <span>Resume exam</span>
            <ArrowRight className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            onClick={handleAbandon}
            disabled={isAbandoning}
            className="w-full sm:w-auto h-10 font-semibold border-border hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30 rounded-xl gap-1.5 text-xs text-muted-foreground transition-all"
          >
            {isAbandoning ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
            <span>Abandon and start fresh</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
